/**
 * WCC360 v2 — assessment state types. Shared by both age bands.
 *
 * The store is intentionally narrow: it holds only the raw answers a
 * learner has given so far. Derived values (effective floor, capacity,
 * archetype tally, low-confidence slots) are computed from these.
 */
import type { Band } from "./band";
import type { HobbyFrequency } from "./causes";
import type { ApprovedObserverInput } from "./observer";


// ---- Part 1: Where you're starting from ---------------------------------

export type Qualification = "school" | "diploma" | "bachelors" | "masters";

/** Study appetite the learner *says* they have (screen 2). */
export type StudyAppetite = "none" | "short_6mo" | "part_time_1_2y" | "full_degree";

/** Reality check when a real start date is attached (screen 3).
 *
 * CANON-PENDING — UNRATIFIED AMENDMENT TO X-STUDY-002.
 * `study_and_work` ("I'd do both — study and earn at the same time") is a
 * fifth option added on Yv's approval and not yet ratified in the Item
 * Bank. It maps to part-time study capacity. Rationale: someone who
 * intends to study while earning previously had to pick "I'd find a
 * shorter route in" or "I'd start earning with them", and both
 * under-report their real study capacity. See the option site in
 * `screens/Part1Screens.tsx` and the mappings below.
 */
export type RealityCheck =
  | "would_sign_up"
  | "want_numbers"
  | "study_and_work"   // CANON-PENDING amendment to X-STUDY-002
  | "cant_money"
  | "cant_time"
  | "cant_neither";


/** Salary floor bands (LPA, INR). 18+ only — teenagers have no anchor for an
 *  absolute salary, and an LPA band at 16 is a guess dressed as data. */
export type SalaryBand = "2_5" | "5_10" | "10_20" | "20_plus";

/** How much flex on the number for the right work (screen 5, 18+ only). */
export type SalaryFlex = "none" | "small_10" | "meaningful_25" | "not_driving";

/** Where the learner can work (screen 6, 18+ only). */
export type WorkLocation = "anywhere" | "my_city" | "remote_only";

// ---- 15–18 band ---------------------------------------------------------

/** Class 11/12 stream. `undecided` routes to the provisional-stream screen. */
export type Stream = "pcm" | "pcb" | "commerce" | "arts" | "vocational" | "undecided";

/** X-STREAM-002 — what school feels like, when no stream is chosen yet.
 *  Produces a PROVISIONAL stream. Never presented as a decision they made. */
export type StreamLean =
  | "maths_physics" | "bio_chem" | "business" | "humanities" | "hands_on" | "none";

/** X-PAY-001, 15–18 band. Relative only — no LPA figures. */
export type MoneyMatters = "most" | "bit_less" | "lot_less" | "not_thinking";

/** X-MOBILITY-001 — how far from home they could study. */
export type StudyMobility = "world" | "india" | "state" | "home" | "unsure";

/**
 * X-BACKGROUND-001 — where a working adult wants their next move to go.
 * Bounded affinity only, and only for "deepen"/"adjacent": a field someone
 * wants out of is never boosted. See `background.ts`.
 */
export type CareerIntent = "deepen" | "adjacent" | "switch" | "unsure";

// ---- 12–14 band ---------------------------------------------------------

/** Context only. Stored, shown back, never scored and never a filter. */
export type SchoolYear = "6" | "7" | "8" | "9" | "other";

export interface Part1Answers {
  qualification?: Qualification;
  study_appetite?: StudyAppetite;
  reality_check?: RealityCheck;
  salary_floor?: SalaryBand;
  salary_flex?: SalaryFlex;
  years_experience?: number;      // 0..40+
  work_location?: WorkLocation;
  // 15–18 band
  stream?: Stream;
  stream_lean?: StreamLean;
  /** True when the stream came from X-STREAM-002, not from the student. */
  stream_provisional?: boolean;
  money_matters?: MoneyMatters;
  study_mobility?: StudyMobility;
  /**
   * X-BACKGROUND-001, 18+ only, and only for someone with paid experience.
   * `professional_field` is an id from `PROFESSIONAL_FIELDS`. Bounded
   * affinity, never an eligibility filter. See `background.ts`.
   */
  professional_field?: string;
  career_intent?: CareerIntent;
  // 12–14 band — both context only, neither ever scored.
  school_year?: SchoolYear;
  what_you_like?: string[];
  /**
   * "What lights you up" — up to seven passion ids, or `["none"]`.
   * Shared by all three bands. Bounded effect only: an archetype nudge and
   * a small family-affinity bonus. Never an eligibility filter.
   * See `passions.ts`.
   */
  passion_ids?: string[];
  /**
   * "Your career matches" chapter. Both scored, both bounded, neither ever
   * an eligibility filter — see `careerPull.ts`.
   * CANON-PENDING: X-DREAM-001 / X-PULL-001.
   */
  childhood_dream?: string;
  career_pull_ids?: string[];
  /**
   * X-HOBBY-002 — how often the learner actually does each activity they
   * picked, keyed by passion id. A multiplier on affinity they already had,
   * never new affinity, never an eligibility filter. See `causes.ts`.
   * CANON-PENDING.
   */
  hobby_frequency?: Record<string, HobbyFrequency>;
  /**
   * X-CAUSE-001 — the learner's top three global issues, in their order of
   * priority. Ranked because everyone claims all fourteen. Bounded affinity
   * plus the cause-led next steps on the result. CANON-PENDING.
   */
  cause_rank_ids?: string[];
  /**
   * X-CAUSE-ROLE-001 — for each of the top two ranked causes, how the
   * learner would most like to contribute (cause id → role id). A small,
   * capped archetype nudge plus the Purpose Map. Never an eligibility filter.
   * CANON-PENDING.
   */
  cause_roles?: Record<string, string>;
  /** Free-text "Something else" cause. Unscored; kept for the counsellor. */
  cause_other?: string;
  /**
   * X-OBSERVER-001 — the Second Opinion. What a parent, guardian or mentor
   * observed, AFTER the learner approved it item by item. Affinity only, at
   * half weight, hard-capped, and never read by the eligibility gate: an
   * observer can widen the work a learner sees and can never change who the
   * engine says they are. Absent unless the learner explicitly approved
   * something. See `observer.ts`. CANON-PENDING.
   */
  observer_input?: ApprovedObserverInput;

}


/** X-PAY-001 option weights. Identical scale to `prefIncomeUpside`. */
export const MONEY_MATTERS_VALUE: Record<MoneyMatters, number> = {
  most: 85,
  bit_less: 60,
  lot_less: 30,
  not_thinking: 15,
};

/** X-STREAM-002 → the provisional stream it implies. */
export const LEAN_TO_STREAM: Record<StreamLean, Stream> = {
  maths_physics: "pcm",
  bio_chem: "pcb",
  business: "commerce",
  humanities: "arts",
  hands_on: "vocational",
  none: "undecided",
};


// ---- Chapters -----------------------------------------------------------

export type ChapterId =
  | "starting" | "how_you_work" | "what_youd_carry" | "lights_you_up"
  | "can_already_do"
  | "career_matches" | "where_going"
  // The ported v1 instrument.
  | "see_yourself" | "what_pulls_you";


export interface Chapter {
  id: ChapterId;
  title: string;
  blurb: string;
  screens: string[];  // screen ids in order
}

// ---- Whole state --------------------------------------------------------

export interface AssessmentV2State {
  band: Band;
  currentScreen: string;          // e.g. "p1_qualification"
  part1: Part1Answers;
  // Part 2 answers land here in later milestones.
  part2: Record<string, unknown>;
  startedAt: string;              // ISO
  updatedAt: string;              // ISO
}

// ---- Derived helpers ----------------------------------------------------

/** Map StudyAppetite → ordinal tier (higher = more study capacity). */
export const STUDY_APPETITE_RANK: Record<StudyAppetite, number> = {
  none: 0,
  short_6mo: 1,
  part_time_1_2y: 2,
  full_degree: 3,
};

/** Map RealityCheck → the equivalent study appetite when a real start
 *  date is attached. "want_numbers" collapses to "short_6mo" — a signal
 *  of hesitancy, not full commitment. */
export const REALITY_TO_APPETITE: Record<RealityCheck, StudyAppetite> = {
  would_sign_up: "full_degree",
  want_numbers: "short_6mo",
  // CANON-PENDING (X-STUDY-002 amendment): studying while earning is real
  // part-time capacity, not "none" and not a full degree.
  study_and_work: "part_time_1_2y",
  cant_money: "none",
  cant_time: "none",
  cant_neither: "none",
};

/**
 * 15–18 band. Same slot, same MIN rule, same four ranks — the difference is
 * that a teenager saying "I'd find a shorter route in" is describing a
 * diploma, not giving up. At 18+ the equivalent answers mean the study
 * genuinely can't happen; at 16 school is still ahead of them either way.
 */
export const REALITY_TO_APPETITE_15_18: Record<RealityCheck, StudyAppetite> = {
  would_sign_up: "full_degree",
  want_numbers: "short_6mo",
  study_and_work: "part_time_1_2y",  // CANON-PENDING amendment to X-STUDY-002
  cant_time: "short_6mo",   // "I'd find a shorter route in"
  cant_money: "none",       // "I'd start earning with them"
  cant_neither: "none",
};


export function realityMapFor(band: Band): Record<RealityCheck, StudyAppetite> {
  return band === "15to18" ? REALITY_TO_APPETITE_15_18 : REALITY_TO_APPETITE;
}

/** Take the MIN of stated appetite and reality-check appetite. This is
 *  the hard filter used against career entry requirements. */
export function effectiveStudyCapacity(
  a: Part1Answers,
  band: Band = "18plus",
): StudyAppetite | null {
  if (!a.study_appetite || !a.reality_check) return a.study_appetite ?? null;
  const stated = STUDY_APPETITE_RANK[a.study_appetite];
  const revealed = STUDY_APPETITE_RANK[realityMapFor(band)[a.reality_check]];
  const min = Math.min(stated, revealed);
  return (Object.keys(STUDY_APPETITE_RANK) as StudyAppetite[])
    .find((k) => STUDY_APPETITE_RANK[k] === min) ?? "none";
}


/** Midpoint of the salary band in LPA. */
export const SALARY_BAND_MIDPOINT_LPA: Record<SalaryBand, number> = {
  "2_5": 17.5,
  "5_10": 37.5,
  "10_20": 75,
  "20_plus": 125,
};

/** The one place a salary band is turned into words. Screens, admin review
 *  and exports all read this, so a band can never say two different things.
 *  The keys are legacy codes held stable so stored answers keep their meaning. */
export const SALARY_BAND_LABEL: Record<SalaryBand, string> = {
  "2_5": "₹10–25 LPA",
  "5_10": "₹25–50 LPA",
  "10_20": "₹50 LPA–₹1 Cr",
  "20_plus": "₹1–1.5 Cr",
};

/** Label for a stored band value, tolerant of unknown/absent data. */
export function salaryBandLabel(band?: string | null): string | null {
  if (!band) return null;
  return SALARY_BAND_LABEL[band as SalaryBand] ?? null;
}

export const SALARY_FLEX_PCT: Record<SalaryFlex, number> = {
  none: 0,
  small_10: 0.10,
  meaningful_25: 0.25,
  not_driving: 1.0, // effectively removes the floor
};

/** effective_floor = floor × (1 − flex). Returns LPA. */
export function effectiveSalaryFloorLpa(a: Part1Answers): number | null {
  if (!a.salary_floor) return null;
  const floor = SALARY_BAND_MIDPOINT_LPA[a.salary_floor];
  const flex = a.salary_flex ? SALARY_FLEX_PCT[a.salary_flex] : 0;
  return Math.max(0, floor * (1 - flex));
}
